import React, { useState, useEffect } from "react";

import Navbar        from "../components/Navbar";
import FilterBar     from "../components/FilterBar";
import ProductCard   from "../components/ProductCard";
import ProductModal  from "../components/ProductModal";

import { getProducts, getCollections, getStoreSettings, incrementProductView } from "../firebase/firestoreService";
import { useAuth } from "../context/AuthContext";
import "../styles/productListing.css";

export default function ProductListing({ setCartOpen, addToCart, cartCount }) {
  const { isAdmin, isDeliveryStaff, isOwner } = useAuth();
  const [products,         setProducts]         = useState([]);
  const [collections,      setCollections]      = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [activeCollection, setActiveCollection] = useState("All");
  const [selectedProduct,  setSelectedProduct]  = useState(null);

  const handleProductView = (product) => {
    setSelectedProduct(product);
    if (!isOwner && !isAdmin && !isDeliveryStaff) {
      incrementProductView(product.id);
    }
  };
  const [settings,         setSettings]         = useState({
    cardStyle:        "standard",
    listingMode:      "collections",
    productView:      "inline",
    sectionTitleStyle: "bold",
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [prods, cols, sett] = await Promise.all([
          getProducts(), getCollections(), getStoreSettings(),
        ]);
        setProducts([...prods].sort(() => Math.random() - 0.5));
        setCollections([...cols].sort(() => Math.random() - 0.5).map((c) => c.name));
        setSettings(sett);
      } catch (err) {
        console.error("Failed to load products:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const compact        = settings.cardStyle        === "compact";
  const sections       = settings.listingMode      === "sections";
  const useModal       = compact || settings.productView === "modal";
  const titleStyle     = settings.sectionTitleStyle || "bold";

  // Filter by active collection in both modes
  const filtered = products.filter((p) => activeCollection === "All" || p.collection === activeCollection);

  // Section mode: group by product.section
  const grouped = sections
    ? filtered.reduce((acc, p) => {
        const key = p.section || "General";
        if (!acc[key]) acc[key] = [];
        acc[key].push(p);
        return acc;
      }, {})
    : null;

  const renderGrid = (items) => (
    <div className={`product-listing__grid ${compact ? "product-listing__grid--compact" : ""}`}>
      {items.map((product) => (
        <ProductCard
          key={product.id}
          product={product}
          compact={compact}
          onAddToCart={addToCart}
          onCardClick={useModal ? handleProductView : null}
          onImageClick={!useModal ? handleProductView : null}
        />
      ))}
    </div>
  );

  return (
    <div className="product-listing">
      <Navbar cartCount={cartCount} onCartClick={() => setCartOpen(true)} />

      <main className="product-listing__main">
        <FilterBar
          collections={collections}
          activeCollection={activeCollection}
          setActiveCollection={setActiveCollection}
        />

        {loading ? (
          <div className="product-listing__loading">
            <div className="product-listing__spinner" />
            <p>Loading products…</p>
          </div>
        ) : sections ? (
          Object.keys(grouped).length === 0 ? (
            <p className="product-listing__empty">No products available yet.</p>
          ) : (
            Object.entries(grouped).map(([sectionName, items]) => (
              <div key={sectionName} className="product-listing__section">
                <h2 className={`product-listing__section-title product-listing__section-title--${titleStyle}`}>{sectionName}</h2>
                {renderGrid(items)}
              </div>
            ))
          )
        ) : filtered.length > 0 ? (
          renderGrid(filtered)
        ) : (
          <p className="product-listing__empty">
            {products.length === 0 ? "No products available yet." : "No products match your filters."}
          </p>
        )}
      </main>

      <footer className="product-listing__footer">
        <p className="product-listing__footer-txt">© 2025 Good Vibes Store · Malawi 🇲🇼</p>
      </footer>

      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onAddToCart={addToCart}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </div>
  );
}
